
import * as React from 'react';
import { IControlOverrideOption } from '../../models/controls/ControlFacet';
import { createMemoizer } from '../../utilities/Memoizer';
import { IComponentAs, IComponentAsProps } from '@uifabric/utilities/lib/IComponentAs';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';

/**
 * Represents props for a component which accepts a default renderer.
 * @public
 * @deprecated Use `Pick<IComponentAsProps<TProps>, 'defaultRender'>` instead.
 */
export type IDefaultRenderProps<TProps> = Pick<IComponentAsProps<TProps>, 'defaultRender'>;

/**
 * @public
 * @deprecated Use `IComponentAsProps<TProps>` instead.
 */
export type IPropsWithDefaultRender<TProps> = IComponentAsProps<TProps>;

/**
 * Represents a component type which is an override for a default
 * version of the component.
 * @public
 * @deprecated Use `IComponentAs<TProps>` instead.
 */
export type IOverrideComponentType<TProps> = IComponentAs<TProps>;

function createComposedComponent<TProps>(outer: IComponentAs<TProps>): (inner: IComponentAs<TProps>) => IComponentAs<TProps> {
  const Outer = outer;

  const outerMemoizer = createMemoizer((inner: IComponentAs<TProps>) => {
    if (outer === inner) {
      throw new Error('Attempted to compose a component with itself.');
    }

    const Inner = inner;

    const innerMemoizer = createMemoizer((defaultRender: IComponentAs<TProps>) => {
      function InnerWithDefaultRender(innerProps: IComponentAsProps<TProps>): JSX.Element {
        return (
          <Inner { ...innerProps } defaultRender={ defaultRender } />
        );
      }

      return InnerWithDefaultRender;
    });

    function OuterWithDefaultRender(outerProps: IComponentAsProps<TProps>): JSX.Element {
      const {
        defaultRender
      } = outerProps;

      return (
        <Outer
          { ...outerProps }
          defaultRender={
            defaultRender ? innerMemoizer(defaultRender) : Inner
          }
        />
      );
    }

    return OuterWithDefaultRender;
  });

  return outerMemoizer;
}

function createComposedItemCacheComponent<TProps>(outer: IComponentAs<TProps>): (inner: IComponentAs<TProps>) => IComponentAs<TProps> {
  const Outer = outer;

  const outerMemoizer = createMemoizer((inner: IComponentAs<TProps>) => {
    if (outer === inner) {
      throw new Error('Attempted to compose a component with itself.');
    }

    const Inner = inner;

    const innerMemoizer = createMemoizer((defaultRender: IComponentAs<TProps>) => {
      const InnerWithDefaultRender = connectToItemCache((
        itemCache: IItemCache,
        innerProps: IComponentAsProps<TProps>
      ): Pick<IComponentAsProps<TProps>, 'defaultRender'> => {
        return {
          defaultRender
        };
      })(Inner);

      InnerWithDefaultRender.displayName = 'InnerWithDefaultRender';

      return InnerWithDefaultRender;
    });

    const OuterWithDefaultRender = connectToItemCache((
      itemCache: IItemCache,
      outerProps: IComponentAsProps<TProps>
    ): Pick<IComponentAsProps<TProps>, 'defaultRender'> => {
      const {
        defaultRender
      } = outerProps;

      return {
        defaultRender: defaultRender ? innerMemoizer(defaultRender) : Inner
      };
    })(Outer);

    OuterWithDefaultRender.displayName = 'OuterWithDefaultRender';

    return OuterWithDefaultRender;
  });

  return outerMemoizer;
}

/**
 * Defines a control option to compose a component type.
 * This simplifies the process of emitting a component type in a control handler callback,
 * ensuring each emitted type gets access to the original type via the defaultRender props.
 * @public
 */
export function defaultRenderComponentOption<TProps>(options: {
  /**
   * Whether or not to assume components are created using `connectToItemCache` and thus
   * may be collapsed when composed.
   */
  useItemCache?: boolean;
} = {}): IControlOverrideOption<IComponentAs<TProps>> {
  const {
    useItemCache = false
  } = options;

  const memoizer = createMemoizer<IComponentAs<TProps>, (inner: IComponentAs<TProps>) => IComponentAs<TProps>>(
    useItemCache ?
      createComposedItemCacheComponent :
      createComposedComponent
  );

  return {
    merge(
      previous: IComponentAs<TProps> | undefined,
      current: IComponentAs<TProps> | undefined
    ): IComponentAs<TProps> | undefined {
      return current && previous &&
        typeof current === 'function' && typeof previous === 'function' &&
        memoizer(current)(previous) ||
        current ||
        previous;
    }
  };
}
